import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useParams} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {
  Box,
  IconButton,
  LinearProgress,
  Snackbar,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import {useGetDocumentQuery} from '../../data/documentWorkspaceApi';
import {useParsePDFMutation} from '../../data/pdfReviewApi';
import {setDocument} from '../../data/documents';
import {selectEffectiveOrg} from '../../data/session';
import {useGetOrgUsersQuery} from '../../data/api';

import {
  PdfReviewDrawer,
  useNutrientViewer,
  usePdfFootingCheck,
  usePdfInconsistencyCheck,
  useCommentNavigation,
} from '../../components/pdf-review';
import {useCommentVisibility} from '../../components/pdf-review/hooks/useCommentVisibility';
import {setVisibilityErrorHandler} from '../../components/pdf-review/store/commentVisibilityStore';
import Notification from '../../components/common/Notification';
import CommentNavigationBar from '../../components/pdf-review/CommentNavigationBar';
import {SCREENS} from '../../components/pdf-review/PdfReviewDrawer';
import {useFootingOverlays} from '../../components/pdf-review/hooks/useFootingOverlays';
import {useInconsistencyOverlays} from '../../components/pdf-review/hooks/useInconsistencyOverlays';
import AIAssistantFloatingButton from '../../components/report-workspace/AIAssistantFloatingButton';
import {hideIntercomIcon, showIntercomIcon} from '../../util';
import {APP_HEADER_HEIGHT, COPILOT_DRAWER_WIDTH_MIN} from '../../theme';
import UserAvatars from '../../components/report-workspace/UserAvatars';
import PrintDropdownButton from '../../components/report-workspace/PrintDropdownButton';
import usePdfAuthToken from '../../components/pdf-review/hooks/usePdfAuthToken';
import ShareDocumentButton from '../../components/documents/ShareDocumentButton';

const CUSTOM_TOOLBAR_WIDTH = 500;
const PARSE_POLL_INTERVAL_MS = 10000; // Poll every 10s while waiting for PDF parse
const WATERMARK_TEXT = 'PRELIMINARY DRAFT -\nSUBJECT TO CHANGE';

// export a pdf buffer without internal comments, using NutrientViewer standalone headless mode
const exportPdfWithoutInternalComments = async (
  instance,
  NutrientViewer,
  licenseKey
) => {
  await instance.save();
  const instantJson = await instance.exportInstantJSON();

  const internalIds = new Set(
    (instantJson.annotations || [])
      .filter(
        (a) => a.isCommentThreadRoot && a.customData?.isInternal !== false
      )
      .map((a) => String(a.id))
      .filter(Boolean)
  );

  // if no internal comments, export the pdf directly
  if (internalIds.size === 0) {
    return instance.exportPDF();
  }

  // clean pdf without annotations and comments
  const cleanPdf = await instance.exportPDF({
    excludeAnnotations: true,
    includeComments: false,
  });

  const {pdfId: _, ...jsonWithoutPdfId} = instantJson;
  // filter out internal comments/annotations
  const filteredJson = {
    ...jsonWithoutPdfId,
    annotations: (instantJson.annotations || []).filter(
      (a) => !internalIds.has(String(a.id))
    ),
    comments: (instantJson.comments || []).filter(
      (c) => !internalIds.has(String(c.rootId))
    ),
  };

  const hiddenContainer = document.createElement('div');
  hiddenContainer.style.cssText =
    'position:fixed;left:-9999px;top:-9999px;width:1px;height:1px;overflow:hidden;';
  document.body.appendChild(hiddenContainer);
  let tempViewerLoaded = false;

  try {
    const tempInstance = await NutrientViewer.load({
      document: cleanPdf,
      instantJSON: filteredJson,
      container: hiddenContainer,
      headless: true,
      useCDN: true,
      licenseKey,
    });
    tempViewerLoaded = true;
    return await tempInstance.exportPDF();
  } finally {
    if (tempViewerLoaded) {
      try {
        await NutrientViewer.unload(hiddenContainer);
      } catch (e) {
        console.warn(
          '[PdfReviewPage] Failed to unload temporary Nutrient viewer:',
          e
        );
      }
    }
    hiddenContainer.remove();
  }
};

const styles = {
  container: {
    display: 'flex',
    width: '100%',
    height: `calc(100vh - ${APP_HEADER_HEIGHT})`,
    position: 'relative',
    marginTop: APP_HEADER_HEIGHT,
    overflow: 'hidden',
  },
  pdfContainer: {
    flexGrow: 1,
    height: '100%',
    backgroundColor: '#F5F5F5',
    position: 'relative',
    '--custom-toolbar-width': `${CUSTOM_TOOLBAR_WIDTH}px`,
  },
  customToolbar: {
    position: 'absolute',
    top: 0,
    right: 0,
    width: CUSTOM_TOOLBAR_WIDTH,
    height: '48px', // height of the nutrient toolbar
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '12px',
    paddingRight: '16px',
    backgroundColor: '#fff',
    zIndex: 1,
    boxSizing: 'border-box',
    borderBottom: '1px solid #e0e0e0',
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    zIndex: 10,
  },
  loadingContent: {
    width: '100%',
    maxWidth: 480,
    px: 3,
  },
  loadingTitle: {
    background:
      'linear-gradient(90deg, #162523 0%, #538B83 21.93%, #233B38 41.08%)',
    backgroundClip: 'text',
    color: 'transparent',
    fontSize: '1.25rem',
    fontWeight: 600,
    textAlign: 'center',
    marginBottom: 2,
  },
  loadingProgress: {
    height: 8,
    borderRadius: 4,
    backgroundColor: '#E0E0E0',
    '& .MuiLinearProgress-bar': {
      backgroundColor: '#34A290',
    },
  },
  loadingStatusText: {
    color: '#67716E',
    marginTop: 1.5,
    fontSize: '14px',
    fontWeight: 400,
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    padding: '24px',
  },
  errorText: {
    color: '#DA3E30',
    fontSize: '16px',
    fontWeight: 500,
    marginBottom: '8px',
  },
  errorSubtext: {
    color: '#67716E',
    fontSize: '14px',
    textAlign: 'center',
  },
  restrictedBanner: {
    position: 'absolute',
    top: 48, // Below the Nutrient toolbar
    left: 0,
    right: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '10px 16px',
    backgroundColor: '#FEF3E2',
    borderBottom: '1px solid #F5A623',
    zIndex: 2,
  },
  restrictedBannerIcon: {
    color: '#B45309',
    fontSize: '18px',
  },
  restrictedBannerText: {
    color: '#92400E',
    fontSize: '14px',
    fontWeight: 500,
  },
  restrictedBannerClose: {
    color: '#B45309',
    padding: '4px',
    marginLeft: 'auto',
    '&:hover': {
      backgroundColor: 'rgba(180, 83, 9, 0.1)',
    },
  },
};

const getLoadingStatusMessage = (isParsing, isViewerLoading) => {
  if (isParsing) return 'Parsing tables';
  if (isViewerLoading) return 'Scanning document...';
  return 'Scanning document...';
};

const LOADING_PROGRESS_MAX = 100;
const LOADING_PROGRESS_INTERVAL_MS = 1500;
const LOADING_PROGRESS_STEP = 10;

const getCreatorDisplayName = (user) => {
  if (!user) return null;
  const first = user.firstName?.trim();
  const last = user.lastName?.trim();
  if (first || last) return [first, last].filter(Boolean).join(' ');
  if (user.email) return user.email;
  return null;
};

const getPdfDownloadFilename = (documentName) => {
  const base =
    (documentName || 'document').replace(/[^\w\s.-]/gi, '').trim() ||
    'document';
  return base.endsWith('.pdf') ? base : `${base}.pdf`;
};

// download a pdf buffer to the browser
const downloadPdfBuffer = (buffer, filename) => {
  const blob = new Blob([buffer], {type: 'application/pdf'});
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const PdfReviewPage = () => {
  const {documentId} = useParams();
  const dispatch = useDispatch();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeScreen, setActiveScreen] = useState(SCREENS.HOME);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [isExportingWatermarkedPdf, setIsExportingWatermarkedPdf] =
    useState(false);
  const [isRestrictionBannerDismissed, setIsRestrictionBannerDismissed] =
    useState(false);
  const [toastError, setToastError] = useState(null);
  const hasTriggeredParseRef = useRef(false);
  const isPdfParsedRef = useRef(false);
  const containerRef = useRef(null);
  const preDrawerZoomRef = useRef(null);

  const {isAuthenticated} = useSelector((state) => state.session);
  const effectiveOrg = useSelector(selectEffectiveOrg);

  const {data: orgUsersData} = useGetOrgUsersQuery(
    {orgId: effectiveOrg?.orgId},
    {skip: !effectiveOrg?.orgId || !isAuthenticated}
  );
  const {users: orgUsers = []} = orgUsersData || {};

  // Transform org users to Nutrient mentionableUsers format
  const mentionableUsers = useMemo(
    () =>
      orgUsers.map((user) => ({
        id: user.userId,
        name: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        displayName:
          `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
        description: user.email,
      })),
    [orgUsers]
  );

  const {data: documentData, isSuccess: documentIsSuccess} =
    useGetDocumentQuery(
      {documentId},
      {
        skip: !documentId || !effectiveOrg?.orgId || !isAuthenticated,
        refetchOnMountOrArgChange: true,
        pollingInterval: isPdfParsedRef.current // need to poll documents while waiting for PDF parse to complete
          ? undefined
          : PARSE_POLL_INTERVAL_MS,
      }
    );

  const isPdfParsed = !!documentData?.record?.ingestionState?.sourceDocumentKey;
  isPdfParsedRef.current = isPdfParsed;

  useEffect(() => {
    hasTriggeredParseRef.current = false;
  }, [documentId]);

  const [parsePDF] = useParsePDFMutation();

  useEffect(() => {
    if (
      documentIsSuccess &&
      !isPdfParsed &&
      !hasTriggeredParseRef.current &&
      documentId
    ) {
      hasTriggeredParseRef.current = true;
      parsePDF({documentId}).catch((err) => {
        console.error('[PdfReviewPage] Background parse trigger failed:', err);
      });
    }
  }, [documentIsSuccess, isPdfParsed, documentId, parsePDF]);

  useEffect(() => {
    if (documentIsSuccess && documentData?.record) {
      dispatch(setDocument({document: documentData.record}));
    }
  }, [documentIsSuccess, documentData, dispatch]);

  const currentUser = useSelector((state) => state.session.currentUser);
  const creatorDisplayName = useMemo(
    () => getCreatorDisplayName(currentUser),
    [currentUser]
  );

  const serverUrl = process.env.REACT_APP_DOC_ENGINE_URL?.replace(
    /\/api\/?$/,
    '/'
  );

  const {
    token: authToken,
    isLoading: isAuthLoading,
    error: authError,
  } = usePdfAuthToken(documentId);

  // Nutrient viewer hook
  const {
    isReady: isViewerReady,
    isLoading: isViewerLoading,
    error: viewerError,
    instance,
    NutrientViewer,
    isDocumentRestricted,
  } = useNutrientViewer({
    containerRef,
    documentId,
    serverUrl,
    authToken,
    creatorName: creatorDisplayName ?? undefined,
    mentionableUsers,
    onReady: (instance) => {},
    onError: (err) => {
      console.error('[PdfReviewPage] Viewer error:', err);
    },
  });

  // Comment visibility (internal / external)
  useCommentVisibility({instance, isReady: isViewerReady});

  useEffect(() => {
    setVisibilityErrorHandler((message) => setToastError(message));
    return () => setVisibilityErrorHandler(null);
  }, []);
  const {
    currentIndex: commentIndex,
    totalCount: commentCount,
    goToNext: goToNextComment,
    goToPrev: goToPrevComment,
  } = useCommentNavigation({instance, NutrientViewer, isReady: isViewerReady});

  // Adjust zoom when drawer opens/closes so comments don't overflow
  const DRAWER_ZOOM_LEVEL = 0.9;
  useEffect(() => {
    if (!instance || !isViewerReady) return;

    if (drawerOpen) {
      preDrawerZoomRef.current = instance.currentZoomLevel;
      instance.setViewState((vs) =>
        vs.set('zoom', Math.min(instance.currentZoomLevel, DRAWER_ZOOM_LEVEL))
      );
    } else if (preDrawerZoomRef.current !== null) {
      instance.setViewState((vs) =>
        vs.set(
          'zoom',
          Math.max(instance.currentZoomLevel, preDrawerZoomRef.current)
        )
      );
      preDrawerZoomRef.current = null;
    }
  }, [drawerOpen, instance, isViewerReady]);

  const {
    showFootingOverlays,
    setActiveFooting,
    clearFootingOverlays,
    scrollToFooting,
  } = useFootingOverlays({
    instance,
    NutrientViewer,
  });

  const {
    showInconsistencyOverlays,
    setActiveInconsistency,
    clearInconsistencyOverlays,
    scrollToInconsistency,
  } = useInconsistencyOverlays({
    instance,
    NutrientViewer,
  });

  const {
    status: footingStatus,
    footingsData,
    error: footingError,
    isLoading: isFootingLoading,
    isParsing,
    isComplete: isFootingComplete,
    rerunFootingsCheck,
    dismissFooting,
    startCheck: startFootingsCheck,
  } = usePdfFootingCheck({documentId, isPdfParsed});

  const {
    status: inconsistencyStatus,
    inconsistenciesData,
    error: inconsistencyError,
    isComplete: isInconsistencyComplete,
    rerunInconsistenciesCheck,
    dismissInconsistency,
    startCheck: startInconsistenciesCheck,
  } = usePdfInconsistencyCheck({documentId, isPdfParsed});

  // Hide/show Intercom
  useEffect(() => {
    try {
      if (isViewerReady) {
        hideIntercomIcon();
      }
    } catch (error) {
      console.error('Error hiding Intercom:', error);
    }

    return () => {
      try {
        showIntercomIcon();
      } catch (error) {
        console.error('Error showing Intercom:', error);
      }
    };
  }, [isViewerReady]);

  const footingsArray = useMemo(
    () => (footingsData?.items ? Object.values(footingsData.items) : []),
    [footingsData?.items]
  );

  const inconsistenciesArray = useMemo(
    () =>
      inconsistenciesData?.openItems
        ? Object.values(inconsistenciesData.openItems)
        : [],
    [inconsistenciesData?.openItems]
  );

  const handleFootingClick = useCallback(
    (footing) => {
      scrollToFooting(footing);
      setActiveFooting(footing.id);
    },
    [scrollToFooting, setActiveFooting]
  );

  useEffect(() => {
    if (!isViewerReady) return;

    if (
      activeScreen === SCREENS.FOOTING_CHECK &&
      isFootingComplete &&
      footingsArray.length > 0
    ) {
      showFootingOverlays(footingsArray, null, handleFootingClick);
    } else {
      clearFootingOverlays();
    }
  }, [
    activeScreen,
    isViewerReady,
    isFootingComplete,
    footingsArray,
    showFootingOverlays,
    clearFootingOverlays,
    handleFootingClick,
  ]);

  const handleFootingSelect = useCallback(
    (footing) => {
      if (isViewerReady && footing) {
        scrollToFooting(footing);
        setActiveFooting(footing.id);
      }
    },
    [isViewerReady, scrollToFooting, setActiveFooting]
  );

  const handleRerunFootingsCheck = useCallback(() => {
    clearFootingOverlays();
    rerunFootingsCheck();
  }, [clearFootingOverlays, rerunFootingsCheck]);

  const handleFootingDismiss = useCallback(
    (footingId) => {
      dismissFooting(footingId);
    },
    [dismissFooting]
  );

  const handleInconsistencyDismiss = useCallback(
    (inconsistencyId) => {
      dismissInconsistency(inconsistencyId);
    },
    [dismissInconsistency]
  );

  // Inconsistency overlays effect
  useEffect(() => {
    if (!isViewerReady) return;

    if (
      activeScreen === SCREENS.INCONSISTENCY_CHECK &&
      isInconsistencyComplete &&
      inconsistenciesArray.length > 0
    ) {
      showInconsistencyOverlays(inconsistenciesArray, null);
    } else {
      clearInconsistencyOverlays();
    }
  }, [
    activeScreen,
    isViewerReady,
    isInconsistencyComplete,
    inconsistenciesArray,
    showInconsistencyOverlays,
    clearInconsistencyOverlays,
  ]);

  const handleInconsistencySelect = useCallback(
    (inconsistency) => {
      if (isViewerReady && inconsistency) {
        scrollToInconsistency(inconsistency);
        setActiveInconsistency(inconsistency.id);
      }
    },
    [isViewerReady, scrollToInconsistency, setActiveInconsistency]
  );

  const handleLocationSelect = useCallback(
    (inconsistency, item, index) => {
      const grounding = item?.cell_grounding ?? item?.grounding;
      if (isViewerReady && grounding) {
        // Scroll to specific location item
        const {page: pageIndex, box} = grounding;
        if (instance && NutrientViewer && pageIndex != null && box) {
          const pageInfo = instance.pageInfoForIndex(pageIndex);
          if (pageInfo) {
            const rect = new NutrientViewer.Geometry.Rect({
              left: box.left * pageInfo.width,
              top: box.top * pageInfo.height,
              width: (box.right - box.left) * pageInfo.width,
              height: (box.bottom - box.top) * pageInfo.height,
            });
            instance.jumpToRect(pageIndex, rect);
          }
        }
        setActiveInconsistency(inconsistency.id);
      }
    },
    [isViewerReady, instance, NutrientViewer, setActiveInconsistency]
  );

  const handleRerunInconsistenciesCheck = useCallback(() => {
    clearInconsistencyOverlays();
    rerunInconsistenciesCheck();
  }, [clearInconsistencyOverlays, rerunInconsistenciesCheck]);

  const toggleDrawer = useCallback(() => {
    setDrawerOpen((prev) => !prev);
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  const handleDownloadPdfWithComments = useCallback(
    async (_event, closeMenu) => {
      if (!instance || !NutrientViewer) return;
      setIsExportingPdf(true);
      try {
        const buffer = await exportPdfWithoutInternalComments(
          instance,
          NutrientViewer,
          process.env.REACT_APP_NUTRIENT_LICENSE_KEY
        );

        downloadPdfBuffer(
          buffer,
          getPdfDownloadFilename(documentData?.record?.name)
        );
      } catch (err) {
        console.error('[PdfReviewPage] PDF export failed:', err);
        setToastError(
          err?.message || 'Failed to export PDF. Please try again.'
        );
      } finally {
        setIsExportingPdf(false);
        closeMenu?.();
      }
    },
    [instance, NutrientViewer, documentData?.record?.name, setToastError]
  );

  const handleDownloadWatermarkedPdf = useCallback(
    async (_event, closeMenu) => {
      if (!instance || !NutrientViewer) return;
      setIsExportingWatermarkedPdf(true);
      try {
        const pdfBuffer = await exportPdfWithoutInternalComments(
          instance,
          NutrientViewer,
          process.env.REACT_APP_NUTRIENT_LICENSE_KEY
        );

        const hiddenContainer = document.createElement('div');
        hiddenContainer.style.cssText =
          'position:fixed;left:-9999px;top:-9999px;width:1px;height:1px;overflow:hidden;';
        document.body.appendChild(hiddenContainer);
        let tempViewerLoaded = false;

        let buffer;
        try {
          const tempInstance = await NutrientViewer.load({
            document: pdfBuffer,
            container: hiddenContainer,
            headless: true,
            useCDN: true,
            licenseKey: process.env.REACT_APP_NUTRIENT_LICENSE_KEY,
          });
          tempViewerLoaded = true;

          // Create watermark text annotations on every page
          const watermarks = [];
          for (let i = 0; i < tempInstance.totalPageCount; i++) {
            const pageInfo = tempInstance.pageInfoForIndex(i);
            if (!pageInfo) continue;
            const pageWidth = pageInfo.width;
            const pageHeight = pageInfo.height;
            // Text wraps before rotation; use diagonal width to avoid unexpected wraps at 45deg.
            const diagonal = Math.sqrt(
              pageWidth * pageWidth + pageHeight * pageHeight
            );
            const boxLeft = (pageWidth - diagonal) / 2;

            watermarks.push(
              new NutrientViewer.Annotations.TextAnnotation({
                pageIndex: i,
                boundingBox: new NutrientViewer.Geometry.Rect({
                  left: boxLeft,
                  top: 0,
                  width: diagonal,
                  height: pageHeight,
                }),
                text: {format: 'plain', value: WATERMARK_TEXT},
                opacity: 0.2,
                fontColor: NutrientViewer.Color.GRAY,
                fontSize: Math.round(Math.min(pageWidth, pageHeight) / 12),
                font: 'Arial',
                horizontalAlign: 'center',
                verticalAlign: 'center',
                rotation: 45,
                readOnly: true,
              })
            );
          }

          const created = await tempInstance.create(watermarks);
          const watermarkIds = created.map((a) => a.id);
          await tempInstance.applyOperations([
            {type: 'flattenAnnotations', annotationIds: watermarkIds},
          ]);
          buffer = await tempInstance.exportPDF();
        } finally {
          if (tempViewerLoaded) {
            try {
              await NutrientViewer.unload(hiddenContainer);
            } catch (e) {
              console.warn(
                '[PdfReviewPage] Failed to unload temporary Nutrient viewer:',
                e
              );
            }
          }
          hiddenContainer.remove();
        }

        downloadPdfBuffer(
          buffer,
          getPdfDownloadFilename(documentData?.record?.name)
        );
      } catch (err) {
        console.error('[PdfReviewPage] Watermarked PDF export failed:', err);
        setToastError(
          err?.message || 'Failed to export watermarked PDF. Please try again.'
        );
      } finally {
        setIsExportingWatermarkedPdf(false);
        closeMenu?.();
      }
    },
    [instance, NutrientViewer, documentData?.record?.name, setToastError]
  );

  const showLoading =
    isAuthLoading || isViewerLoading || (isFootingLoading && !isViewerReady);
  const displayError = authError || viewerError;

  useEffect(() => {
    setLoadingProgress(0);
    setIsRestrictionBannerDismissed(false);
  }, [documentId]);

  // Simulate progress bar fill while loading
  useEffect(() => {
    if (!showLoading) return;
    const timer = setInterval(() => {
      setLoadingProgress((prev) =>
        prev >= LOADING_PROGRESS_MAX
          ? LOADING_PROGRESS_MAX
          : prev + LOADING_PROGRESS_STEP
      );
    }, LOADING_PROGRESS_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [showLoading]);

  if (displayError) {
    return (
      <Box sx={{...styles.container, justifyContent: 'center'}}>
        <Box sx={styles.errorContainer}>
          <Typography sx={styles.errorText}>
            {authError ? 'Authentication failed' : 'Failed to load PDF viewer'}
          </Typography>
          <Typography sx={styles.errorSubtext}>
            {displayError?.message || 'Please try refreshing the page.'}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={styles.container}>
      {/* PDF Viewer Container */}
      <Box
        ref={containerRef}
        data-drawer-open={drawerOpen}
        sx={{
          ...styles.pdfContainer,
          '--pdf-drawer-margin': drawerOpen
            ? `${COPILOT_DRAWER_WIDTH_MIN}px`
            : '0px',
        }}
      >
        {isViewerReady && (
          <Box sx={styles.customToolbar}>
            {commentCount > 0 && (
              <CommentNavigationBar
                currentIndex={commentIndex}
                totalCount={commentCount}
                onNext={goToNextComment}
                onPrev={goToPrevComment}
              />
            )}
            <ShareDocumentButton documentId={documentId} isPdfReviewDoc />
            <UserAvatars />
            <PrintDropdownButton
              menuItems={[
                {
                  key: 'title1',
                  type: 'title',
                  title: 'DOWNLOAD AS...',
                  visible: true,
                },
                {
                  key: 'pdf',
                  onClick: handleDownloadPdfWithComments,
                  title: isExportingPdf
                    ? 'Downloading...'
                    : 'PDF Document (.pdf)',
                  type: 'item',
                  disabled: isExportingPdf || isExportingWatermarkedPdf,
                  manualClose: true,
                },
                {
                  key: 'watermarked-pdf',
                  onClick: handleDownloadWatermarkedPdf,
                  title: isExportingWatermarkedPdf
                    ? 'Downloading...'
                    : 'Watermarked PDF (.pdf)',
                  type: 'item',
                  disabled: isExportingPdf || isExportingWatermarkedPdf,
                  manualClose: true,
                },
              ]}
            />
          </Box>
        )}
        {isViewerReady &&
          isDocumentRestricted &&
          !isRestrictionBannerDismissed && (
            <Box sx={styles.restrictedBanner}>
              <LockOutlinedIcon sx={styles.restrictedBannerIcon} />
              <Typography sx={styles.restrictedBannerText}>
                This PDF has editing restrictions. Annotations and text
                selection are disabled.
              </Typography>
              <IconButton
                sx={styles.restrictedBannerClose}
                onClick={() => setIsRestrictionBannerDismissed(true)}
                size="small"
                aria-label="Dismiss restriction notice"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
      </Box>

      {showLoading && (
        <Box sx={styles.loadingOverlay}>
          <Box sx={styles.loadingContent}>
            <Typography sx={styles.loadingTitle}>
              Getting your PDF ready
            </Typography>
            <LinearProgress
              variant="determinate"
              value={loadingProgress}
              sx={styles.loadingProgress}
            />
            <Typography sx={styles.loadingStatusText}>
              {getLoadingStatusMessage(isParsing, isViewerLoading)}
            </Typography>
          </Box>
        </Box>
      )}

      <AIAssistantFloatingButton
        isVisible={!drawerOpen}
        onClick={toggleDrawer}
        isOpen={drawerOpen}
      />

      <PdfReviewDrawer
        open={drawerOpen}
        onClose={closeDrawer}
        footingStatus={footingStatus}
        footingsData={footingsData}
        footingError={footingError}
        onRerunFootingsCheck={handleRerunFootingsCheck}
        onFootingSelect={handleFootingSelect}
        onStartFootingsCheck={startFootingsCheck}
        inconsistencyStatus={inconsistencyStatus}
        inconsistenciesData={inconsistenciesData}
        inconsistencyError={inconsistencyError}
        onRerunInconsistenciesCheck={handleRerunInconsistenciesCheck}
        onInconsistencySelect={handleInconsistencySelect}
        onLocationSelect={handleLocationSelect}
        onStartInconsistenciesCheck={startInconsistenciesCheck}
        isPdfParsed={isPdfParsed}
        onScreenChange={setActiveScreen}
        documentId={documentId}
        onFootingDismiss={handleFootingDismiss}
        onFootingDismissError={setToastError}
        onInconsistencyDismiss={handleInconsistencyDismiss}
        onInconsistencyDismissError={setToastError}
      />

      <Snackbar
        open={!!toastError}
        autoHideDuration={6000}
        onClose={() => setToastError(null)}
        anchorOrigin={{vertical: 'top', horizontal: 'center'}}
        sx={{top: '54px !important'}}
      >
        <Notification
          status="error"
          title="Something went wrong"
          subtitle={toastError}
          onClose={() => setToastError(null)}
        />
      </Snackbar>
    </Box>
  );
};

export default PdfReviewPage;
